// src/managerPortal/pages/ReservationsPage.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";  // <-- Make sure to import useNavigate
import SidebarMenu from "../components/SidebarMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../styles/ReservationManagement.css";

// Buttons
import AddButton from "../components/common/AddButton";
import EditButton from "../components/common/EditButton";
import DeleteButton from "../components/common/DeleteButton";

// Modals
import ReservationModal from "../components/modals/ReservationModal";
import DeleteReservationModal from "../components/modals/DeleteReservationModal";

// Import our dynamic API URL (must be at the top with other imports)
import apiUrl from "../../config";

// Import the reusable Pagination component
import Pagination from "../components/Pagination";

/**
 * Utility function to format a 10-digit phone number as (xxx) xxx - xxxx.
 * If the string isn't exactly 10 digits, it is returned as-is.
 */
const formatPhoneNumber = (phoneString) => {
    const cleaned = ("" + phoneString).replace(/\D/g, "");
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phoneString;
};

const capitalizeName = (name) => {
    if (!name) return "";
    return name
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ");
};

const ReservationsPage = () => {
    const navigate = useNavigate();

    const [reservations, setReservations] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedDate, setSelectedDate] = useState(""); // For date filtering
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [newlyCreatedId, setNewlyCreatedId] = useState(null);

    // Sorting
    const [sortField, setSortField] = useState("date");
    const [sortOrder, setSortOrder] = useState("desc");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10); // Items per page selector

    // Modals
    const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("add"); // "add" or "edit"

    // ─────────────────────────────────────────────────────────────────────────────
    // Fetch reservations on mount, with 403 check
    // ─────────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/manager/reservations`, {
                    withCredentials: true,
                });
                setReservations(response.data);
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    navigate("/manager/no-access");
                } else {
                    console.error("Error fetching data:", error);
                }
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [navigate]);

    // ─────────────────────────────────────────────────────────────────────────────
    // Merged: Scroll to highlighted row and remove highlight after 5 seconds
    // ─────────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (newlyCreatedId) {
            const highlightedRow = document.getElementById(newlyCreatedId);
            if (highlightedRow) {
                highlightedRow.scrollIntoView({ behavior: "smooth", block: "center" });
            }

            const timer = setTimeout(() => {
                setNewlyCreatedId(null);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [newlyCreatedId]);

    // ─────────────────────────────────────────────────────────────────────────────
    // Open/Close Modals
    // ─────────────────────────────────────────────────────────────────────────────
    const openReservationModal = (reservation = null) => {
        setSelectedReservation(reservation);
        setModalMode(reservation ? "edit" : "add");
        setIsReservationModalOpen(true);
    };

    const closeReservationModal = () => {
        setIsReservationModalOpen(false);
        setSelectedReservation(null);
    };

    const openDeleteModal = (reservation) => {
        setSelectedReservation(reservation);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedReservation(null);
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Save (Add/Edit) Reservation
    // ─────────────────────────────────────────────────────────────────────────────
    const handleSaveReservation = async (reservationData) => {
        try {
            if (modalMode === "edit" && selectedReservation) {
                await axios.put(
                    `${apiUrl}/api/manager/reservations/${selectedReservation._id}`,
                    reservationData,
                    { withCredentials: true }
                );
            } else {
                // Create new reservation and capture its ID
                const response = await axios.post(
                    `${apiUrl}/api/manager/reservations`,
                    reservationData,
                    { withCredentials: true }
                );
                setNewlyCreatedId(response.data._id);
            }
            // Refresh the reservations list
            const updated = await axios.get(`${apiUrl}/api/manager/reservations`, {
                withCredentials: true,
            });
            setReservations(updated.data);
            closeReservationModal();
        } catch (error) {
            console.error("Error saving reservation:", error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // Delete Reservation
    // ─────────────────────────────────────────────────────────────────────────────
    const handleDeleteReservation = async () => {
        if (!selectedReservation) return;
        try {
            await axios.delete(
                `${apiUrl}/api/manager/reservations/${selectedReservation._id}`,
                { withCredentials: true }
            );
            // Refresh the list
            const updated = await axios.get(`${apiUrl}/api/manager/reservations`, {
                withCredentials: true,
            });
            setReservations(updated.data);
            closeDeleteModal();
        } catch (error) {
            console.error("Error deleting reservation:", error);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // FILTER & SORT RESERVATIONS
    // ─────────────────────────────────────────────────────────────────────────────
    const filteredReservations = reservations.filter((res) => {
        const name = res.name ?? "";
        const email = res.email ?? "";
        const phone = res.phone ?? "";

        const matchesSearch =
            !searchQuery ||
            [name, email, phone].some((field) =>
                field.toLowerCase().includes(searchQuery.toLowerCase())
            );

        let matchesDate = true;
        if (selectedDate) {
            const resDate = new Date(res.date).toISOString().slice(0, 10);
            matchesDate = resDate === selectedDate;
        }

        return matchesSearch && matchesDate;
    });

    // Sorting (by date)
    const sortedReservations = [...filteredReservations].sort((a, b) => {
        if (!a[sortField] || !b[sortField]) return 0;
        if (sortField === "date") {
            return sortOrder === "asc"
                ? new Date(a.date) - new Date(b.date)
                : new Date(b.date) - new Date(a.date);
        }
        return 0;
    });

    const handleSort = (field) => {
        let order = "asc";
        if (sortField === field && sortOrder === "asc") {
            order = "desc";
        }
        setSortField(field);
        setSortOrder(order);
        const reSorted = [...reservations].sort((a, b) => {
            if (!a[field] || !b[field]) return 0;
            if (order === "asc") {
                return new Date(a[field]) - new Date(b[field]);
            } else {
                return new Date(b[field]) - new Date(a[field]);
            }
        });
        setReservations(reSorted);
    };

    const clearFilters = () => {
        setSearchQuery("");
        setSelectedDate("");
        setCurrentPage(1);
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // PAGINATION LOGIC
    // ─────────────────────────────────────────────────────────────────────────────
    const indexOfLastReservation = currentPage * itemsPerPage;
    const indexOfFirstReservation = indexOfLastReservation - itemsPerPage;
    const currentReservations = sortedReservations.slice(
        indexOfFirstReservation,
        indexOfLastReservation
    );
    const totalPages = Math.ceil(sortedReservations.length / itemsPerPage);

    // ─────────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────────
    return (
        <div className="layout">
            <SidebarMenu />
            <div className="main-content">
                {/* Card Container */}
                <div className="card">
                    <div className="card-header">
                        <h4>Reservation List</h4>
                    </div>

                    <div className="card-body">
                        {/* Search + Date Filter + Add Button */}
                        <div className="search-add-container">
                            <div className="search-wrapper">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search by name, email, or phone..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                <input
                                    type="date"
                                    className="date-filter"
                                    value={selectedDate}
                                    onChange={(e) => {
                                        setSelectedDate(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    placeholder="Filter by date"
                                />
                                <button className="btn-clear" onClick={clearFilters}>
                                    Clear
                                </button>
                            </div>
                            <AddButton onClick={() => openReservationModal()} />
                        </div>

                        {/* Reservations Table */}
                        <div className="table-responsive">
                            <table className="events-table">
                                <thead>
                                <tr>
                                    <th className="sortable" onClick={() => handleSort("date")}>
                      <span className="sortable-header">
                        <span className="label">Date</span>
                          {sortField === "date" && (
                              <FontAwesomeIcon
                                  icon={sortOrder === "asc" ? "sort-up" : "sort-down"}
                              />
                          )}
                      </span>
                                    </th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Guests</th>
                                    <th>Deposit</th>
                                    <th>Special Requests</th>
                                    <th className="actions-col">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentReservations.map((reservation) => (
                                    <tr
                                        key={reservation._id}
                                        id={reservation._id}
                                        className={
                                            reservation._id === newlyCreatedId ? "highlight-row" : ""
                                        }
                                    >
                                        <td>
                                            {new Date(reservation.date).toLocaleDateString("en-US", {
                                                month: "short",
                                                day: "2-digit",
                                                year: "numeric",
                                                timeZone: "UTC",
                                            })}
                                        </td>
                                        <td>{capitalizeName(reservation.name)}</td>
                                        <td>{reservation.email}</td>
                                        <td>{formatPhoneNumber(reservation.phone)}</td>
                                        <td>{reservation.guests}</td>
                                        <td>
                                            {reservation.depositPaid ? (
                                                <FontAwesomeIcon
                                                    icon="check"
                                                    style={{ color: "green", fontSize: "1.2rem" }}
                                                />
                                            ) : (
                                                <FontAwesomeIcon
                                                    icon="xmark"
                                                    style={{ color: "red", fontSize: "1.2rem" }}
                                                />
                                            )}
                                        </td>
                                        <td>{reservation.specialRequests || "None"}</td>
                                        <td className="actions-col">
                                            <div className="action-buttons">
                                                <EditButton
                                                    onClick={() => openReservationModal(reservation)}
                                                />
                                                <DeleteButton
                                                    onClick={() => openDeleteModal(reservation)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Reusable Pagination Component */}
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={(value) => {
                                setItemsPerPage(value);
                                setCurrentPage(1);
                            }}
                            storageKey="reservationsItemsPerPage"  // This key uniquely identifies the setting for this page
                        />

                    </div>
                </div>
            </div>

            {/* Reservation Modal */}
            {isReservationModalOpen && (
                <ReservationModal
                    show={isReservationModalOpen}
                    mode={modalMode}
                    reservationData={selectedReservation}
                    onClose={closeReservationModal}
                    onSave={handleSaveReservation}
                />
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <DeleteReservationModal
                    itemName={selectedReservation?.name}
                    itemDate={
                        selectedReservation?.date
                            ? new Date(selectedReservation.date).toLocaleDateString("en-US", {
                                timeZone: "UTC",
                            })
                            : ""
                    }
                    onClose={closeDeleteModal}
                    onConfirm={handleDeleteReservation}
                />
            )}
        </div>
    );
};

export default ReservationsPage;
